import { Link, useNavigate, useParams } from "react-router";
import { useEffect } from "react";
import { findMeaningById, findWordById, meaningsForWord } from "../../domain/vocabulary/grouping.ts";
import { relationsFrom, relationTargetLabel } from "../../domain/vocabulary/relations.ts";
import { useAppServices } from "../hooks/AppContext.tsx";
import { useNow, useVocabularySnapshot } from "../hooks/bindings.ts";
import { Frame, PrimaryButton, Screen, TopBar } from "../components/Shell.tsx";
import { TypeTag } from "../components/TypeTag.tsx";
import styles from "../design-system/ui.module.css";

export function MeaningCardScreen() {
  const { meaningId = "" } = useParams();
  const snapshot = useVocabularySnapshot();
  const { generation, commands, queries } = useAppServices();
  const navigate = useNavigate();
  const now = useNow();
  const meaning = findMeaningById(snapshot, meaningId);
  const word = meaning ? findWordById(snapshot, meaning.wordId) : undefined;
  const siblings = meaning ? meaningsForWord(snapshot, meaning.wordId) : [];

  useEffect(() => {
    if (!meaningId) {
      return;
    }
    void commands.recordMeaningExposure(meaningId);
  }, [commands, meaningId]);

  if (!meaning || !word) {
    return (
      <Frame>
        <Screen withNav={false}>
          <TopBar title="My Word" backTo="/words" />
          <div className={styles.empty}>This meaning is no longer saved.</div>
        </Screen>
      </Frame>
    );
  }

  const relations = relationsFrom(snapshot, meaning.id);

  return (
    <Frame>
      <Screen withNav={false}>
        <TopBar
          title="My Word"
          backTo={siblings.length > 1 ? `/words/${word.id}` : "/words"}
          right={
            <button className={styles.textButton} type="button" onClick={() => navigate(`/meanings/${meaning.id}/edit`)}>
              Edit
            </button>
          }
        />
        <h1 className={styles.heroWord}>
          {word.displayText} <TypeTag type={meaning.type} />
        </h1>
        <p className={styles.meaningTitle}>{meaning.shortLabel}</p>
        <section className={styles.detail}>
          <h2>Meaning</h2>
          <p>{meaning.definition}</p>
        </section>
        {meaning.russianHint ? (
          <section className={styles.detail}>
            <h2>Russian hint</h2>
            <p>{meaning.russianHint}</p>
          </section>
        ) : null}
        <section className={styles.detail}>
          <h2>Example</h2>
          <p>{meaning.example}</p>
        </section>
        {meaning.collocations.length ? (
          <section className={styles.detail}>
            <h2>Common patterns</h2>
            <div className={styles.tags}>
              {meaning.collocations.map((item) => (
                <span className={styles.tag} key={item}>
                  {item}
                </span>
              ))}
            </div>
          </section>
        ) : null}
        {relations.length ? (
          <section className={styles.detail}>
            <h2>Similar words</h2>
            {relations.map((relation) => (
              <p key={relation.targetMeaningId} style={{ marginBottom: 8 }}>
                <Link to={`/meanings/${relation.targetMeaningId}`}>
                  {relationTargetLabel(snapshot, relation.targetMeaningId)}
                </Link>
                <br />
                {relation.explanation}
              </p>
            ))}
          </section>
        ) : null}
        {meaning.memoryKey ? (
          <section className={`${styles.detail} ${styles.memory}`}>
            <h2>Memory key</h2>
            <p>{meaning.memoryKey}</p>
          </section>
        ) : null}
        <div className={styles.practiceStatus}>
          {(() => {
            const lines = queries.scheduleLines(meaning.id, now);
            return (
              <>
                <p>{lines.written}</p>
                <p>{lines.hideMeaning}</p>
                <p>{lines.hideWord}</p>
              </>
            );
          })()}
          <p>These dates belong to this meaning only.</p>
        </div>
        {meaning.practiceReadiness === "needs_regeneration" ? (
          <div className={styles.banner}>
            Practice for this meaning resumes after you regenerate exercises and save the new package.
          </div>
        ) : null}
        <div className={styles.stack} style={{ marginTop: 22 }}>
          <PrimaryButton
            secondary
            onClick={() => {
              generation.addAnotherMeaning(word.id);
              navigate("/add/choose");
            }}
          >
            + Add another meaning
          </PrimaryButton>
          {siblings.length > 1 ? (
            <button className={styles.textButton} type="button" onClick={() => navigate(`/words/${word.id}`)}>
              View all {siblings.length} saved meanings
            </button>
          ) : null}
          <button
            className={styles.textButton}
            type="button"
            onClick={async () => {
              if (window.confirm(`Delete the “${meaning.shortLabel}” meaning of “${word.displayText}”?`)) {
                await commands.deleteMeaning(meaning.id);
                navigate(siblings.length > 1 ? `/words/${word.id}` : "/words");
              }
            }}
          >
            Delete this meaning
          </button>
        </div>
      </Screen>
    </Frame>
  );
}
